import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditRole from "./AddEditRole";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import RoleActionButton from "./RoleActionButton";
import Confirmation from "../../../components/Confirmation";

const Roles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [roleId, setRoleId] = useState(null);
  const [showAddEditRole, setShowAddEditRole] = useState(false);
  const [showRoleDelete, setShowRoleDelete] = useState(false);

  const toggleShowAddEditRole = () => {
    if (showAddEditRole) {
      setEdit(false);
      setRoleId(null);
    }
    setShowAddEditRole(!showAddEditRole);
  };

  const toggleShowDelete = () => {
    if (showRoleDelete) {
      setRoleId(null);
    }
    setShowRoleDelete(!showRoleDelete);
  };

  const fetchRole = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_ROLE_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const rolesList = data.data.data;
        setRolesList(rolesList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setRolesList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const rolesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: rolesList[i]?.id,
        name: rolesList[i]?.name ? rolesList[i]?.name : "-",
        type: rolesList[i]?.type ? rolesList[i]?.type : "-",
        status: (
          <RoleActionButton
            id={rolesList[i]?.id}
            statusMode={true}
            status={rolesList[i]?.status}
            setLoading={setLoading}
            fetchRoleData={() => fetchRole(limit, page)}
            roleData={rolesList[i]}
          />
        ),
        action: (
          <>
            <RoleActionButton
              id={rolesList[i]?.id}
              setEdit={setEdit}
              setRoleId={setRoleId}
              toggleShowAddEditRole={toggleShowAddEditRole}
              toggleShowDelete={toggleShowDelete}
              fetchRoleData={() => fetchRole(limit, page)}
              roleData={rolesList[i]}
            />
          </>
        ),
      });
    }
    return items;
  };

  const roles = rolesGenerator(rolesList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "type",
      text: "Type",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "status",
      text: "Status",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchRole(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchRole(limit, page);
  };

  // Single Role Delete
  const handleRoleDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_ROLE_LIST}/${roleId}`);
      setLoading(false);
      fetchRole();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add Role */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Role" : "Add Role"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setRoleId(null);
                setShowAddEditRole(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Role</span>
                </div>
              }
              className={"sm:max-w-3/4"}
              open={showAddEditRole}
              buttonClassName={""}
              setOpen={setShowAddEditRole}
            >
              <AddEditRole
                edit={edit}
                toggleShowAddEditRole={toggleShowAddEditRole}
                id={roleId}
                fetchRoleData={() => fetchRole(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={roles}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Role"}
        open={showRoleDelete}
        setOpen={setShowRoleDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete role?"}
        okAction={async () => {
          handleRoleDelete();
          toggleShowDelete();
          fetchRole();
        }}
        cancelAction={() => {
          setRoleId(null);
          setShowRoleDelete(!showRoleDelete);
        }}
      />
    </>
  );
};

export default Roles;
